<template>
  <div>
    <!-- Drawer -->
    <transition name="slide">
      <div v-if="isVisible" class="onboarding-drawer">
        <!-- Header -->
        <div class="drawer-header">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="d-flex align-items-center mb-2">
                <div class="category-icon mr-3">
                  <i :class="currentCategory.icon"></i>
                </div>
                <h4 class="mb-0 drawer-title">{{ currentCategory.name }}</h4>
              </div>
              <p class="small mb-0 header-subtitle">{{ currentCategory.description || 'Complete all tasks in this category' }}</p>
            </div>
            <div class="header-actions">
              <el-tooltip content="Dismiss setup assistant" effect="light" :open-delay="300" placement="bottom">
                <button class="close-btn" @click="dismissOnboarding">
                  <i class="tim-icons icon-simple-remove"></i>
                </button>
              </el-tooltip>
              <el-tooltip content="Collapse panel" effect="light" :open-delay="300" placement="left">
                <button class="close-btn" @click="closePanel">
                  <i class="tim-icons icon-minimal-right"></i>
                </button>
              </el-tooltip>
            </div>
          </div>

          <!-- Progress -->
          <div class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="small progress-label">{{ completedTasks }} of {{ totalTasks }} completed</span>
              <span class="small progress-label progress-label--strong">{{ completionPercentage }}%</span>
            </div>
            <div class="progress drawer-progress-track" style="height: 6px;">
              <div
                class="progress-bar"
                :class="completionPercentage === 100 ? 'bg-success' : 'bg-primary'"
                role="progressbar"
                :style="{ width: completionPercentage + '%' }">
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="drawer-tabs">
          <button
            class="tab-pill"
            :class="{ active: activeTab === 'tasks' }"
            @click="activeTab = 'tasks'">
            Tasks
          </button>
          <button
            class="tab-pill"
            :class="{ active: activeTab === 'assistant' }"
            @click="activeTab = 'assistant'">
            Assistant
          </button>
        </div>

        <!-- Body -->
        <div class="drawer-body" :class="{ 'no-padding': activeTab === 'assistant' }">
          <!-- Tasks tab -->
          <template v-if="activeTab === 'tasks'">
            <div v-if="!currentCategory.tasks || currentCategory.tasks.length === 0" class="empty-state">
              <i class="tim-icons icon-components empty-state-icon"></i>
              <p class="empty-state-text mt-2">No tasks available</p>
            </div>

            <div v-else class="tasks-list">
              <div
                v-for="task in currentCategory.tasks"
                :key="task.id"
                class="task-item"
                :ref="task.id == activeTaskId ? 'activeTask' : undefined"
                :class="{
                  'completed': task.is_completed,
                  'active': task.id == activeTaskId,
                  'locked': task.status === 'locked',
                  'blocked': task.status === 'blocked',
                  'permission-locked': task.permission_locked
                }"
              >
                <div class="task-content">
                  <h6 class="task-name"
                      :class="{ 'task-link': !task.permission_locked && task.id != activeTaskId }"
                      @click="!task.permission_locked && task.id != activeTaskId && navigateToTask(task)">
                    {{ task.name }}
                    <i v-if="task.permission_locked" class="tim-icons icon-lock-circle ml-1 task-lock-icon"
                       title="Your role doesn't have access to this task"></i>
                    <i v-else-if="navigatingTaskId === task.id" class="fas fa-spinner fa-spin ml-1 task-name-spinner"></i>
                  </h6>
                  <p class="task-description">{{ task.description }}</p>

                  <!-- Inline steps for active task -->
                  <div v-if="task.id == activeTaskId && task.steps && task.steps.length > 0" class="inline-steps">
                    <template v-for="(block, bIndex) in task.steps">
                      <p v-if="block.type === 'text'" :key="'b-' + bIndex" class="step-text-block">{{ block.content }}</p>

                      <div v-if="block.type === 'steps'" :key="'s-' + bIndex" class="steps-list">
                        <div v-for="(step, sIndex) in block.items" :key="sIndex" class="step-item">
                          <div class="step-number">{{ sIndex + 1 }}</div>
                          <div v-if="sIndex < block.items.length - 1" class="step-connector"></div>
                          <div class="step-text">{{ step }}</div>
                        </div>
                      </div>
                    </template>

                    <div v-if="task.docs_url || (task.is_completed && task.completed_at)" class="task-meta mt-3">
                      <badge
                        v-if="task.is_completed && task.completed_at"
                        type="success"
                        :title="task.completed_by_name ? 'Marked complete by ' + task.completed_by_name : ''">
                        {{ formatCompletionMeta(task) }}
                      </badge>
                      <a
                         v-if="task.docs_url"
                         :href="task.docs_url"
                         target="_blank"
                         class="kb-article-link small"
                         @click.stop>
                        <i class="tim-icons icon-link-72 mr-1"></i>Help article
                      </a>
                    </div>

                    <base-button
                      v-if="!task.permission_locked && !task.is_completed"
                      :loading="togglingTaskId === task.id"
                      :disabled="!!togglingTaskId && togglingTaskId !== task.id"
                      @click.stop="completeActiveTask"
                      variant="outlined"
                      tone="primary"
                      size="sm"
                      class="mt-3">
                      <i v-if="togglingTaskId !== task.id" class="tim-icons icon-check-2 mr-2"></i>
                      Mark as complete
                    </base-button>

                    <base-button
                      v-else-if="!task.permission_locked && task.is_completed"
                      :loading="togglingTaskId === task.id"
                      :disabled="!!togglingTaskId && togglingTaskId !== task.id"
                      @click.stop="completeActiveTask"
                      variant="outlined"
                      tone="success"
                      size="sm"
                      class="mt-3">
                      <i v-if="togglingTaskId !== task.id" class="tim-icons icon-simple-remove mr-2"></i>
                      Mark as incomplete
                    </base-button>

                    <p v-else class="permission-locked-note mt-3 small">
                      <i class="tim-icons icon-lock-circle mr-1"></i>
                      Your role doesn't have access to this task. Ask an administrator to mark it complete.
                    </p>

                  </div>

                  <div v-else-if="task.docs_url || (task.is_completed && task.completed_at)" class="task-meta">
                    <badge
                      v-if="task.is_completed && task.completed_at"
                      type="success"
                      :title="task.completed_by_name ? 'Marked complete by ' + task.completed_by_name : ''">
                      {{ formatCompletionMeta(task) }}
                    </badge>
                    <a
                       v-if="task.docs_url"
                       :href="task.docs_url"
                       target="_blank"
                       class="kb-article-link small"
                       @click.stop>
                      <i class="tim-icons icon-link-72 mr-1"></i>Help article
                    </a>
                  </div>

                  <div v-if="task.id == activeTaskId && !(task.steps && task.steps.length > 0)" class="active-indicator">
                    <span class="badge badge-primary badge-sm">Current task</span>
                  </div>
                </div>

              </div>
            </div>
          </template>

          <!-- Assistant tab -->
          <template v-if="activeTab === 'assistant'">
            <chat-assistant
              mode="embedded"
              :category-id="currentCategory.id"
              :task-id="activeTaskId"
              :current-task="activeTaskObject"
            />
          </template>
        </div>

        <!-- Footer -->
        <div class="drawer-footer">
          <div v-if="completionPercentage === 100" class="completion-message">
            <div class="success-animation">
              <i class="tim-icons icon-check-2"></i>
            </div>
            <h6 class="completion-heading">All tasks completed!</h6>
            <p class="completion-subtitle small">You've successfully completed all tasks in this category.</p>
            <base-button
              @click="returnToWizard"
              type="primary"
              size="sm"
              block>
              Return to setup wizard
            </base-button>
          </div>
          <div v-else class="d-flex justify-content-between align-items-center">
            <base-button
              @click="returnToWizard"
              type="secondary"
              size="sm">
              <i class="tim-icons icon-minimal-left mr-2"></i>
              Back to wizard
            </base-button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Minimized tab -->
    <transition name="fade">
      <div v-if="!isVisible && showMinimized" class="minimized-panel" @click="isVisible = true">
        <el-tooltip content="Dismiss" effect="light" :open-delay="300" placement="left">
          <button class="minimized-dismiss" @click.stop="dismissOnboarding">
            <i class="tim-icons icon-simple-remove"></i>
          </button>
        </el-tooltip>
        <div class="minimized-content">
          <div class="minimized-icon">
            <i class="tim-icons icon-bulb-63"></i>
          </div>
          <div class="minimized-progress">
            <span class="small">{{ completedTasks }}/{{ totalTasks }}</span>
            <div class="progress" style="height: 3px; width: 60px; background: rgba(255, 255, 255, 0.1);">
              <div
                class="progress-bar bg-primary"
                :style="{ width: completionPercentage + '%' }">
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { Badge } from '@/components';
import ChatAssistant from './ChatAssistant.vue';
import { markPermissionLocks } from '@/util/wizardPermissions';

// The onboarding panel "follows" the user across pages while they work through a
// task. That session lives in sessionStorage (not the URL) so an ordinary
// in-app navigation no longer drops the panel; it is cleared only when the user
// explicitly dismisses it or returns to the wizard board. Scoped to the tab
// session on purpose — a fresh tab/restart starts clean.
const ONBOARDING_SESSION_KEY = 'onboardingSession';

export default {
  name: 'OnboardingSidePanel',
  components: {
    Badge,
    ChatAssistant
  },
  data() {
    return {
      isVisible: false,
      showMinimized: false,
      activeTab: 'tasks',
      currentCategory: {
        id: null,
        name: '',
        description: '',
        icon: 'tim-icons icon-components',
        tasks: []
      },
      activeTaskId: null,
      navigatingTaskId: null,
      togglingTaskId: null,
      companyId: localStorage.getItem('companyId')
    };
  },

  computed: {
    totalTasks() {
      return this.currentCategory.tasks?.length || 0;
    },

    completedTasks() {
      return this.currentCategory.tasks?.filter(task => task.is_completed).length || 0;
    },

    completionPercentage() {
      if (this.totalTasks === 0) return 0;
      return Math.round((this.completedTasks / this.totalTasks) * 100);
    },

    activeTaskObject() {
      if (!this.activeTaskId || !this.currentCategory.tasks) return null;
      return this.currentCategory.tasks.find(t => t.id == this.activeTaskId) || null;
    },

  },

  methods: {
    async getAuthToken() {
      try {
        const session = await Auth.currentSession();
        return session.idToken.jwtToken;
      } catch (err) {
        console.error(err);
        return null;
      }
    },

    async loadCategoryTasks(categoryId, taskId) {
      if (!categoryId) return;
      // taskId is passed explicitly when continuing a persisted session (the URL
      // no longer carries it); fall back to the URL on a fresh task launch.
      this.activeTaskId = taskId !== undefined ? taskId : this.$route.query.taskId;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/onboarding/${categoryId}`;

        const response = await axios.get(url, config);
        // Mark tasks the user can't reach as permission_locked, and drop any
        // beta-hidden tasks (e.g. Point of Sale for non-beta companies). When a
        // beta task is dropped, markPermissionLocks recomputes this category's
        // counts so the header denominator matches what is shown.
        const [marked] = markPermissionLocks([response.data]);
        this.currentCategory = marked || response.data;
        this.$nextTick(() => this.scrollToActiveTask());
      } catch (err) {
        console.error('Failed to load category tasks:', err);
      }
    },

    scrollToActiveTask() {
      const el = this.$refs.activeTask;
      if (el && el[0]) {
        el[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    async toggleTask(taskId) {
      if (this.togglingTaskId) return;

      // Defense-in-depth: locked tasks hide the action buttons, but bail out
      // here too in case the toggle is reached another way.
      const task = (this.currentCategory.tasks || []).find(t => t.id === taskId);
      if (task && task.permission_locked) return;

      this.togglingTaskId = taskId;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/onboarding/${taskId}`;

        const response = await axios.post(url, {}, config);
        const isCompleted = response.data.is_completed;
        const wasPreviouslyComplete = this.completionPercentage === 100;

        await this.loadCategoryTasks(this.currentCategory.id, this.activeTaskId);

        if (!wasPreviouslyComplete && this.completionPercentage === 100) {
          this.showCompletionFeedback();
        } else {
          this.$notify({
            type: 'success',
            title: isCompleted ? 'Task completed!' : 'Task marked incomplete',
            message: isCompleted ? 'Task has been marked as complete.' : 'Task has been marked as incomplete.',
            duration: 3000
          });
        }

      } catch (err) {
        console.error('Failed to update task:', err);
        this.$notify({
          type: 'danger',
          title: 'Error',
          message: 'Could not update task. Please try again.',
          duration: 3000
        });
      } finally {
        this.togglingTaskId = null;
      }
    },

    showCompletionFeedback() {
      setTimeout(() => {
        this.$notify({
          type: 'success',
          title: 'Category complete!',
          message: `Congratulations! You've completed all tasks in ${this.currentCategory.name}.`,
          duration: 5000
        });
      }, 500);
    },

    async completeActiveTask() {
      await this.toggleTask(this.activeTaskId);
    },

    async navigateToTask(task) {
      if (this.navigatingTaskId) return;
      this.navigatingTaskId = task.id;

      try {
        await this._resolveAndNavigate(task);
      } finally {
        this.navigatingTaskId = null;
      }
    },

    async _resolveAndNavigate(task) {
      // Parse path and query params from feature_url (e.g. "/purchases/settings?tab=4")
      const [path, queryString] = task.feature_url.split('?');
      const query = {
        onboarding: 'true',
        categoryId: this.currentCategory.id,
        taskId: task.id
      };

      if (queryString) {
        new URLSearchParams(queryString).forEach((v, k) => { query[k] = v; });
      }

      // Resolve group-specific IDs
      const group = JSON.parse(localStorage.getItem('group'));
      if (group && path === '/purchases/settings') {
        query.id = group.payment_settings_id;
      }
      if (group && path === '/photos/settings') {
        query.id = group.photo_settings_id;
      }
      if (group && path === '/mail/automated/settings') {
        query.id = group.automated_email_settings_id;
      }
      if (group && path === '/settings/group') {
        query.id = group.id;
      }

      // For pages that need a game in localStorage (analytics, leaderboard settings, communication screen)
      const gameLocalStoragePages = ['/games/clues/configure/display', '/dashboard/game/leaderboard/settings', '/dashboard/game/leaderboard', '/dashboard/game/analytics'];
      if (gameLocalStoragePages.includes(path) && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/games`;
          const response = await axios.get(url, config);
          if (response.data && response.data.length > 0) {
            localStorage.setItem('game', JSON.stringify({ id: response.data[0].id }));
            localStorage.setItem('selectedGameStats', JSON.stringify(response.data[0]));
          }
        } catch (err) {
          console.error('Failed to fetch games:', err);
        }
      }

      // For project board tasks, auto-redirect to first project
      if ((path === '/projects/board' || path === '/projects/ticket') && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/projects?count=1&offset=0`;
          const response = await axios.get(url, config);
          const projects = response.data.projects || response.data;
          if (projects && projects.length > 0) {
            const projectId = projects[0].id;
            // For ticket detail, also fetch first ticket
            if (path === '/projects/ticket') {
              try {
                const ticketsUrl = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/projects/${projectId}/tickets?count=1&offset=0&status=&priority=&type=&assignee=&reporter=&key=&summary=`;
                const ticketsResponse = await axios.get(ticketsUrl, config);
                const tickets = ticketsResponse.data.tickets || ticketsResponse.data;
                if (tickets && tickets.length > 0) {
                  this.$router.push({ path: `/projects/${projectId}/tickets/${tickets[0].id}`, query });
                  return;
                }
              } catch (e) {
                console.error('Failed to fetch tickets:', e);
              }
            }
            this.$router.push({ path: `/projects/${projectId}`, query });
            return;
          }
        } catch (err) {
          console.error('Failed to fetch projects:', err);
        }
        this.$router.push({ path: '/projects', query: { count: 10, page: 1, ...query } });
        return;
      }

      // For waiver settings, auto-redirect to traditional waiver config
      if (path === '/waivers/settings' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/waiver-settings`;
          const response = await axios.get(url, config);
          const types = response.data.waiver_setting_types;
          if (types && types.length > 0) {
            const traditional = types.find(t => t.type === 'traditional');
            query.id = traditional ? traditional.id : types[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch waiver settings:', err);
        }
        this.$router.push({ path: '/waivers/settings', query });
        return;
      }

      // For customer detail tasks: if already on the details page, just switch tab
      if (path === '/customers/details' && this.$route.path === '/customers/details') {
        query.id = this.$route.query.id;
        this.$router.replace({ path: '/customers/details', query }).catch(() => {});
        return;
      }

      // For customer detail tasks, auto-redirect to first customer
      if (path === '/customers/details' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/customers?count=1&offset=0&first_name=&last_name=&email=&phone=&game=&tag=`;
          const response = await axios.get(url, config);
          const customers = response.data.customers;
          if (customers && customers.length > 0) {
            query.id = customers[0].end_user_id;
          }
        } catch (err) {
          console.error('Failed to fetch customers:', err);
        }
        this.$router.push({ path: '/customers/details', query });
        return;
      }

      // For survey edit tasks, auto-redirect to first survey
      if (path === '/surveys/edit' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/surveys`;
          const response = await axios.get(url, config);
          const surveys = response.data.surveys;
          if (surveys && surveys.length > 0) {
            query.id = surveys[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch surveys:', err);
        }
        this.$router.push({ path: '/surveys/edit', query });
        return;
      }

      // For survey results task, auto-redirect to first survey results
      if (path === '/surveys/results' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/surveys`;
          const response = await axios.get(url, config);
          const surveys = response.data.surveys;
          if (surveys && surveys.length > 0) {
            query.id = surveys[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch surveys:', err);
        }
        this.$router.push({ path: '/surveys/results', query });
        return;
      }

      // For transaction detail task, auto-redirect to first completed transaction
      if (path === '/purchases/transactions/details' && !query.id && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/transactions?count=1&offset=0&first_name=&last_name=&email=&payment=&to=&from=&t_id=&status=1&source=`;
          const response = await axios.get(url, config);
          const transactions = response.data.transactions;
          if (transactions && transactions.length > 0) {
            query.id = transactions[0].id;
          }
        } catch (err) {
          console.error('Failed to fetch transactions:', err);
        }
        this.$router.push({ path: '/purchases/transactions/details', query });
        return;
      }

      // /games/history: only deep-link into a specific instance for the
      // tracker-details task. The history-list task stays on the list page.
      if (path === '/games/history' && group) {
        const wantsInstanceDetail = task.id === 51;
        if (wantsInstanceDetail) {
          try {
            const token = await this.getAuthToken();
            const config = { headers: { Authorization: token } };
            const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/game-instances?count=1&offset=0&game=&game_master=&to=&from=`;
            const response = await axios.get(url, config);
            const instances = response.data.gameInstances || response.data;
            if (instances && instances.length > 0) {
              query.id = instances[0].id;
              this.$router.push({ path: '/games/history/instance', query });
              return;
            }
          } catch (err) {
            console.error('Failed to fetch game instances:', err);
          }
        }
        this.$router.push({ path, query });
        return;
      }

      // The Ratings tab lives on the event list page itself, so that task skips both
      // event-editor redirects below and opens /games/settings?tab=ratings.
      const isEventEditTask = path === '/games/settings' && query.tab !== 'ratings';

      // For event edit tasks: if already on the edit page, just switch tab
      if (isEventEditTask && this.$route.path === '/games/edit') {
        query.id = this.$route.query.id;
        this.$router.replace({ path: '/games/edit', query }).catch(() => {});
        return;
      }

      // For event edit tasks that point to the list, auto-redirect to first game's edit page
      if (isEventEditTask && group) {
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${group.id}/games`;
          const response = await axios.get(url, config);
          if (response.data && response.data.length > 0) {
            query.id = response.data[0].id;
          } else {
            query.id = '';
          }
        } catch (err) {
          console.error('Failed to fetch games:', err);
          query.id = '';
        }
        this.$router.push({ path: '/games/edit', query });
        return;
      }

      this.$router.push({ path, query });
    },

    returnToWizard() {
      // Going back to the full wizard board is a clean exit from the
      // follow-along session.
      this.endSession();
      this.$router.push('/wizard');
    },

    closePanel() {
      // Collapse to the minimized tab; the session stays active so the tab
      // keeps following the user until they dismiss it.
      this.isVisible = false;
    },

    readSession() {
      try {
        return JSON.parse(sessionStorage.getItem(ONBOARDING_SESSION_KEY) || 'null');
      } catch {
        return null;
      }
    },

    endSession() {
      sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
      this.isVisible = false;
      this.showMinimized = false;
    },

    dismissOnboarding() {
      this.endSession();
      // Strip the onboarding params from the current URL so a refresh on this
      // page doesn't reopen the panel from the query.
      const query = { ...this.$route.query };
      if (query.onboarding || query.categoryId || query.taskId) {
        delete query.onboarding;
        delete query.categoryId;
        delete query.taskId;
        this.$router.replace({ path: this.$route.path, query }).catch(() => {});
      }
    },

    handleOnboardingRoute(query) {
      // A fresh task launch (from the wizard board or an in-panel task link)
      // carries the params in the URL: persist the session and open the drawer.
      if (query.onboarding === 'true' && query.categoryId) {
        const session = { categoryId: query.categoryId, taskId: query.taskId || null };
        sessionStorage.setItem(ONBOARDING_SESSION_KEY, JSON.stringify(session));
        this.isVisible = true;
        this.showMinimized = true;
        this.activeTaskId = session.taskId;
        this.loadCategoryTasks(session.categoryId, session.taskId);
        return;
      }

      // No params in the URL. Keep the panel alive if a session is still active
      // (the user navigated elsewhere mid-onboarding) rather than dropping it.
      // Respect the user's collapsed/open state — don't force the drawer open on
      // every navigation, just keep the minimized tab present.
      const session = this.readSession();
      // Don't let the follow-along tab hover over the full wizard board itself.
      if (session && session.categoryId && this.$route.path !== '/wizard') {
        this.showMinimized = true;
        this.activeTaskId = session.taskId;
        if (String(this.currentCategory.id) !== String(session.categoryId)) {
          this.loadCategoryTasks(session.categoryId, session.taskId);
        }
      } else {
        this.isVisible = false;
        this.showMinimized = false;
      }
    },

    /**
     * Format completion attribution: "Completed May 25, 2026" (auto-detected)
     * or "Completed May 25, 2026 by Jana Geddis" (manually checked).
     */
    formatCompletionMeta(task) {
      if (!task.is_completed || !task.completed_at) return '';
      const date = new Date(task.completed_at);
      const dateStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      return task.completed_by_name
        ? `Completed ${dateStr} by ${task.completed_by_name}`
        : `Completed ${dateStr}`;
    },
  },

  watch: {
    '$route'(to) {
      this.handleOnboardingRoute(to.query);
    },
    /**
     * When the wizard side panel opens on desktop, push any open content drawers
     * (BookingSlotSettingsDrawer, ResourceDrawer, etc.) aside by setting a CSS
     * variable that BaseDrawer reads. On mobile the wizard panel is full-width,
     * so side-by-side isn't possible — fall back to the existing overlap behavior.
     */
    isVisible: {
      immediate: true,
      handler(visible) {
        if (typeof document === 'undefined') return;
        if (visible && window.innerWidth > 768) {
          document.documentElement.style.setProperty('--wizard-panel-offset', '420px');
        } else {
          document.documentElement.style.removeProperty('--wizard-panel-offset');
        }
      }
    }
  },

  mounted() {
    this.handleOnboardingRoute(this.$route.query);
  },

  beforeDestroy() {
    // Don't leave the offset stranded on :root if this panel is torn down while open
    if (typeof document !== 'undefined') {
      document.documentElement.style.removeProperty('--wizard-panel-offset');
    }
  },
};
</script>

<style lang="scss" scoped>
.onboarding-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  /* 100dvh shrinks with mobile Safari's address+toolbar bars so the footer stays visible;
     100vh fallback keeps older iOS / non-supporting browsers working */
  height: 100vh;
  height: 100dvh;
  background: #171826;
  box-shadow: -10px 0 50px rgba(0, 0, 0, 0.5), -2px 0 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1060;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100vw;
  }
}

.drawer-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(180deg, #2d2d44 0%, #252538 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.category-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.85);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

/* Close button — visual stays 32px, total tap target grows to 44x44 via padding wrapper */
.close-btn {
  width: 44px;
  height: 44px;
  padding: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  i {
    width: 32px;
    height: 32px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  &:hover i {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

/* Inline-style replacements (so .white-content can override) */
.header-subtitle {
  color: rgba(255, 255, 255, 0.7);
}

.drawer-title {
  color: #ffffff;
}

.progress-label {
  color: rgba(255, 255, 255, 0.7);
}

.progress-label--strong {
  color: #ffffff;
}

.drawer-progress-track {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.drawer-tabs {
  display: flex;
  gap: 6px;
  padding: 10px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.tab-pill {
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.9);
  }

  &.active {
    background: rgba(225, 78, 202, 0.15);
    border-color: #e14eca;
    color: #e14eca;
  }
}

.drawer-body {
  flex: 1;
  /* min-height: 0 lets the flex child shrink so overflow-y: auto actually scrolls on iOS Safari */
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 24px;

  &.no-padding {
    padding: 0;
    overflow: hidden;
    /* Assistant tab embeds a flex-column chat panel that needs the body
       to be a flex container so the messages list can claim remaining
       height and scroll, instead of overflowing the body and pushing
       the input below the viewport. */
    display: flex;
    flex-direction: column;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }
}

.drawer-footer {
  /* Pad below the home-indicator gesture bar on iPhones with no hardware home button */
  padding: 16px 24px calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;

  i {
    font-size: 48px;
    margin-bottom: 16px;
  }
}

.empty-state-icon {
  color: rgba(255, 255, 255, 0.45);
}

.empty-state-text {
  color: rgba(255, 255, 255, 0.6);
}

.completion-heading {
  color: #ffffff;
}

.completion-subtitle {
  color: rgba(255, 255, 255, 0.6);
}

/* Tasks List */
.tasks-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
  }

  &.completed {
    opacity: 0.7;
    background: rgba(0, 191, 154, 0.06);
    border-color: rgba(0, 191, 154, 0.15);
  }

  &.active {
    background: rgba(225, 78, 202, 0.08);
    border-color: rgba(225, 78, 202, 0.25);
    box-shadow: 0 2px 12px rgba(225, 78, 202, 0.15);

    // Outlined action buttons (Mark as complete / Mark as incomplete) sit on
    // top of this magenta-tinted card and disappear at their default tint.
    // Render them at their hover-intensity background and brighter text by
    // default so they read as clear actions instead of card chrome.
    ::v-deep .btn-outlined-primary {
      background: rgba(225, 78, 202, 0.32) !important;
      border-color: #e14eca !important;
      color: #fff !important;
    }

    ::v-deep .btn-outlined-success {
      background: rgba(0, 191, 154, 0.32) !important;
      border-color: #00bf9a !important;
      color: #fff !important;
    }
  }

  &.locked {
    background: rgba(59, 130, 246, 0.04);
    border-color: rgba(59, 130, 246, 0.15);
    opacity: 0.7;
  }

  &.blocked {
    background: rgba(251, 191, 36, 0.04);
    border-color: rgba(251, 191, 36, 0.15);
    opacity: 0.8;
  }

  &.permission-locked {
    opacity: 0.55;
  }
}

.task-lock-icon {
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
}

.permission-locked-note {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.55);
  font-style: italic;
  margin-bottom: 0;
}

.task-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* Checkbox */
.task-checkbox {
  flex-shrink: 0;
}

/* Checkbox label — visual stays 20px, tap target grows to 44x44 via padding */
.custom-checkbox {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 12px;
  margin: -12px;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    &:checked ~ .checkbox-mark {
      background: transparent;

      i {
        color: #00f2c3 !important;
        opacity: 1;
      }
    }
  }

  &:hover .checkbox-mark {
    border-color: rgba(255, 255, 255, 0.5);
  }
}

.checkbox-mark {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
  background: transparent;

  /* Icon visibility is CSS-driven (input:checked) so it tracks the click instantly
     instead of waiting for the API round-trip. Bigger + bolder for contrast. */
  i {
    font-size: 14px;
    font-weight: 900;
    color: #ffffff;
    opacity: 0;
    transition: opacity 0.15s ease;
  }
}

.custom-checkbox.is-disabled {
  cursor: not-allowed;
  opacity: 0.45;

  &:hover .checkbox-mark {
    border-color: rgba(255, 255, 255, 0.45);
  }
}

.checkbox-spinner {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #e14eca;
  font-size: 14px;
}

.status-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.status-icon.auto-checked {
  cursor: default;
}

/* Task Content */
.task-content {
  flex: 1;
  min-width: 0;
}

.task-name {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: rgba(255, 255, 255, 0.95);
  text-transform: none;

  &.task-link {
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #e14eca;
    }
  }
}

.task-name-spinner {
  font-size: 11px;
  color: #e14eca;
  vertical-align: middle;
}

.task-description {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin-bottom: 10px;
}


.kb-article-link {
  display: inline-flex;
  align-items: center;
  color: #e14eca;
  text-decoration: none;
  margin-right: 12px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
    color: #e14eca;
  }
}

.active-indicator {
  margin-top: 6px;
}

.completed-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #00f2c3;
  font-size: 12px;
  font-weight: 500;
}

.task-action {
  flex-shrink: 0;
}

/* Completion */
.completion-message {
  text-align: center;
}

.success-animation {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #00bf9a, #00d68f);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  /* Three pulses then stop — long-running infinite animation is distracting */
  animation: pulse 2s ease-in-out 3;

  i {
    font-size: 32px;
    color: white;
  }
}

@keyframes pulse {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 191, 154, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 20px 10px rgba(0, 191, 154, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 191, 154, 0); }
}

/* Minimized tab */
.minimized-panel {
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  background: linear-gradient(135deg, rgba(30, 30, 47, 0.98), rgba(20, 20, 35, 0.98));
  border: 1px solid rgba(225, 78, 202, 0.35);
  border-right: none;
  border-radius: 10px 0 0 10px;
  padding: 12px 8px;
  cursor: pointer;
  z-index: 1050;
  transition: all 0.2s ease;
  box-shadow:
    -8px 0 32px rgba(0, 0, 0, 0.55),
    -2px 0 12px rgba(225, 78, 202, 0.35),
    inset 1px 0 0 rgba(255, 255, 255, 0.06);

  &:hover {
    padding-right: 12px;
    background: linear-gradient(135deg, rgba(40, 40, 57, 0.98), rgba(30, 30, 45, 0.98));
    border-color: rgba(225, 78, 202, 0.55);
    box-shadow:
      -10px 0 36px rgba(0, 0, 0, 0.6),
      -3px 0 18px rgba(225, 78, 202, 0.5),
      inset 1px 0 0 rgba(255, 255, 255, 0.08);
  }
}

/* Dismiss × on the floating tab — corner badge, fully visible tap target */
.minimized-dismiss {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 50%;
  background: #171826;
  border: 1px solid rgba(225, 78, 202, 0.45);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  z-index: 1;

  &:hover {
    color: #fff;
    border-color: #e14eca;
    background: #2d2d44;
  }
}

.minimized-panel:hover .minimized-dismiss,
.minimized-dismiss:focus-visible {
  opacity: 1;
}

.minimized-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.minimized-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: rgba(255, 255, 255, 0.85);

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
}

.minimized-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.6);
}

/* Inline Steps */
.inline-steps {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(225, 78, 202, 0.15);
}

.step-text-block {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 12px;
}

.steps-list {
  position: relative;
  margin-bottom: 12px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  position: relative;
  padding-bottom: 16px;

  &:last-child {
    padding-bottom: 0;
  }
}

.step-number {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  background: rgba(225, 78, 202, 0.15);
  border: 1px solid rgba(225, 78, 202, 0.3);
  color: #e14eca;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.step-connector {
  position: absolute;
  left: 10px;
  top: 22px;
  width: 2px;
  bottom: 0;
  background: rgba(225, 78, 202, 0.15);
}

.step-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 22px;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .minimized-panel {
    display: none;
  }
}
</style>

<style lang="scss">
/* Light mode overrides */
.white-content {
  .onboarding-drawer {
    background: #ffffff;
    box-shadow: -10px 0 50px rgba(0, 0, 0, 0.15), -2px 0 15px rgba(0, 0, 0, 0.08);
  }

  .drawer-header {
    border-bottom-color: #e3e3e3;
    background: linear-gradient(180deg, #f8f9fa 0%, #f5f6fa 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .drawer-body {
    &::-webkit-scrollbar-track {
      background: #f5f6fa;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);

      &:hover {
        background: rgba(0, 0, 0, 0.15);
      }
    }
  }

  .drawer-tabs {
    border-bottom-color: #e3e3e3;
  }

  .tab-pill {
    border-color: #e3e3e3;
    color: #6c757d;

    &:hover {
      border-color: #ced4da;
      color: #344767;
    }

    &.active {
      background: rgba(225, 78, 202, 0.1);
      border-color: #e14eca;
      color: #e14eca;
    }
  }

  .drawer-footer {
    border-top-color: #e3e3e3;
    background: #f8f9fa;
  }

  .task-item {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.08);

    &:hover {
      background: rgba(0, 0, 0, 0.04);
      border-color: rgba(0, 0, 0, 0.12);
    }
  }

  .inline-steps {
    border-top-color: rgba(225, 78, 202, 0.1);
  }

  .step-text-block {
    color: rgba(0, 0, 0, 0.6);
  }

  .step-text {
    color: rgba(0, 0, 0, 0.75);
  }

  /* Drawer header text */
  .drawer-title {
    color: rgba(0, 0, 0, 0.85);
  }

  .header-subtitle,
  .progress-label {
    color: rgba(0, 0, 0, 0.65) !important;
  }

  .progress-label--strong {
    color: rgba(0, 0, 0, 0.85) !important;
  }

  /* Progress track — soft cool gray, not warm. !important because Bootstrap/theme .progress wins on cascade otherwise. */
  .drawer-progress-track {
    background-color: rgba(0, 0, 0, 0.06) !important;
  }

  /* Category icon tile in drawer header */
  .category-icon {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.7);
  }

  /* Close button hover state in light mode */
  // Namespaced to .onboarding-drawer: this block is unscoped (so .white-content
  // on <body> matches), which means a bare `.close-btn` here leaks onto EVERY
  // .close-btn in the app (e.g. BookingDrawer), painting a box behind their
  // icon. Scoping to the panel's own root keeps the style local.
  .onboarding-drawer .close-btn {
    color: rgba(0, 0, 0, 0.5);

    i {
      background: rgba(0, 0, 0, 0.04);
      border-color: rgba(0, 0, 0, 0.1);
    }

    &:hover i {
      background: rgba(0, 0, 0, 0.08);
      color: rgba(0, 0, 0, 0.85);
    }
  }

  /* Task content text */
  .task-name {
    color: rgba(0, 0, 0, 0.85);
  }

  .task-description {
    color: rgba(0, 0, 0, 0.65);
  }


  /* Task status states — keep tinted backgrounds visible on white */
  .task-item.completed {
    background: rgba(0, 191, 154, 0.08);
    border-color: rgba(0, 191, 154, 0.25);
  }

  .task-item.active {
    background: rgba(225, 78, 202, 0.08);
    border-color: rgba(225, 78, 202, 0.35);
    box-shadow: 0 2px 12px rgba(225, 78, 202, 0.12);
  }

  .task-item.locked {
    background: rgba(59, 130, 246, 0.06);
    border-color: rgba(59, 130, 246, 0.25);
  }

  .task-item.blocked {
    background: rgba(251, 191, 36, 0.08);
    border-color: rgba(251, 191, 36, 0.3);
  }

  /* Checkbox border */
  .checkbox-mark {
    border-color: rgba(0, 0, 0, 0.3);
  }

  .custom-checkbox:hover .checkbox-mark {
    border-color: rgba(0, 0, 0, 0.5);
  }

  /* Empty state text */
  .empty-state-icon {
    color: rgba(0, 0, 0, 0.35) !important;
  }

  .empty-state-text {
    color: rgba(0, 0, 0, 0.6) !important;
  }

  /* Completion message */
  .completion-heading {
    color: rgba(0, 0, 0, 0.85);
  }

  .completion-subtitle {
    color: rgba(0, 0, 0, 0.6);
  }

  /* Minimized panel */
  .minimized-panel {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 246, 250, 0.98));
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);

    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(240, 241, 245, 1));
    }
  }

  .minimized-progress {
    color: rgba(0, 0, 0, 0.6);
  }

  .minimized-dismiss {
    background: #ffffff;
    border-color: rgba(225, 78, 202, 0.45);
    color: rgba(0, 0, 0, 0.55);

    &:hover {
      color: #e14eca;
      border-color: #e14eca;
      background: #f5f6fa;
    }
  }
}
</style>
